import { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { FiFilter, FiRotateCcw, FiSearch } from "react-icons/fi";
import { Apis } from "../../../lib/Apis";
import Loader from "../../../Utils/loader";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";

function ManagePermissions() {
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [nameFilter, setNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({});
  const [loadingPermissionId, setLoadingPermissionId] = useState(null);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [pagination, setPagination] = useState({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    perPage: 10,
  });

  const buildParams = useCallback(() => {
    const params = {};
    if (nameFilter.trim()) params.name = nameFilter.trim();
    if (statusFilter !== "") params.status = statusFilter;
    return params;
  }, [nameFilter, statusFilter]);

  const fetchPermissions = useCallback(
    async (nextOffset = offset, overrideParams = appliedFilters) => {
      try {
        setIsLoading(true);
        const response = await Apis.getPermissions(nextOffset, limit, overrideParams);
        const payload = response.data.data;
        setPermissions(payload.permissions || []);
        setPagination(
          payload.pagination || {
            totalRecords: 0,
            totalPages: 0,
            currentPage: 1,
            perPage: limit,
          },
        );
      } catch (error) {
        Swal.fire(
          "Error",
          error.response?.data?.message || "Failed to fetch permissions.",
          "error",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [appliedFilters, offset, limit],
  );

  useEffect(() => {
    fetchPermissions(offset);
  }, [offset, fetchPermissions]);

  const handleApplyFilter = () => {
    const nextFilters = buildParams();
    setAppliedFilters(nextFilters);
    setOffset(0);
    fetchPermissions(0, nextFilters);
  };

  const handleResetFilter = () => {
    setNameFilter("");
    setStatusFilter("");
    setAppliedFilters({});
    setOffset(0);
    fetchPermissions(0, {});
  };

  const handleToggleStatus = async (permission) => {
    try {
      setLoadingPermissionId(permission.id);
      await Apis.togglePermissionStatus(permission.id);
      setPermissions((prevPermissions) =>
        prevPermissions.map((item) =>
          item.id === permission.id ? { ...item, status: !item.status } : item,
        ),
      );
      Swal.fire("Success", "Permission status updated successfully.", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to update permission status.",
        "error",
      );
    } finally {
      setLoadingPermissionId(null);
    }
  };

  if (isLoading) {
    return <Loader message="Loading permissions..." />;
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <h2 className="text-2xl font-bold">Manage Permissions</h2>
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by permission, resource, or module"
            value={nameFilter}
            onChange={(event) => setNameFilter(event.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <select
          className="status-select"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
        >
          <option value="">Select Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleApplyFilter}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button className="btn btn-reset-filter filter-btn" onClick={handleResetFilter}>
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No.</th>
                <th className="table-th">Module</th>
                <th className="table-th">Resource</th>
                <th className="table-th">Action</th>
                <th className="table-th">Status</th>
              </tr>
            </thead>
            <tbody>
              {permissions.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-8">
                    No permissions found.
                  </td>
                </tr>
              ) : (
                permissions.map((permission, index) => (
                  <tr
                    key={permission.id}
                    className={`${index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"} hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">{offset + index + 1}</td>
                    <td className="table-td font-medium">
                      {permission.module || permission.resource}
                    </td>
                    <td className="table-td capitalize">
                      {permission.resource?.replaceAll("_", " ") || "-"}
                    </td>
                    <td className="table-td capitalize">
                      {permission.action?.replaceAll("_", " ") || "-"}
                    </td>
                    <td className="table-td">
                      <div
                        className={`status-toggle ${permission.status ? "active" : ""} ${loadingPermissionId === permission.id ? "loading" : ""}`}
                        onClick={() =>
                          loadingPermissionId !== permission.id &&
                          handleToggleStatus(permission)
                        }
                      >
                        <div className="status-toggle-handle"></div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.totalPages > 1 && (
        <div className="mt-5">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={pagination.totalRecords}
            onPageChange={setOffset}
          />
        </div>
      )}
    </div>
  );
}

export default ManagePermissions;
